import React from 'react';

import { ICurrencyFormat } from 'utils/types';
import { SpendTypeEnum } from 'components/Classification';
import { DimensionList } from 'components/SpendAllocation/DimensionList';
import { AllocationList } from 'components/SpendAllocation/AllocationList';
import { MiddleContent } from 'components/SpendAllocation/MiddleContent';
import { DimensionControls } from 'components/SpendAllocation/DimensionControls';
import { ApButtonTab, colors } from '@alixpartners/ui-components';
import {
  IDimensionSelection,
  IDimensionGroup,
  IAllocatedDimensionGroup,
  IAllocatedItem,
  IProductItem,
  IAllocation,
  DimensionTypeEnum,
} from 'components/SpendAllocation/types';
import {
  isAllocationItemValid,
  contributionField,
  dimensionLabels,
} from 'components/SpendAllocation/utils';
import { TabsWrapper } from './ProductAllocation.styles';
import {
  LeftSection,
  MainWrapper,
  SideContainer,
  MiddleContainer,
  ScrollWrapper,
  Overlay,
  OverlayContainer,
} from '../styles';

interface IProps {
  permissions: string[];
  format: ICurrencyFormat;
  spendType: SpendTypeEnum;
  dimensions: IDimensionGroup[];
  applications: IDimensionGroup[];
  allocations: IAllocatedItem[];
  products: IProductItem[];
  onReuseAllocation(): void;
  onAllocationChange(allocation: IAllocatedItem): void;
  selectedYear: number;
  isDimensionsLoading?: boolean;
}

interface IState {
  selectedItem: IDimensionSelection | null;
  isAppTab: boolean;
}

export class ProductAllocation extends React.Component<IProps, IState> {
  state = {
    selectedItem: null as IState['selectedItem'],
    isAppTab: true,
  };

  handleItemSelect = (selection: IDimensionSelection | null) => {
    this.setState({ selectedItem: selection });
  };

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    const { isAppTab } = this.state;
    const { selectedYear } = this.props;

    if (
      selectedYear !== prevProps.selectedYear ||
      isAppTab !== prevState.isAppTab
    ) {
      this.setState({ selectedItem: null });
    }
  }

  getSelectedParentName = () => {
    const { selectedItem } = this.state;

    if (!selectedItem || !selectedItem.rootId) {
      return null;
    }

    const { dimensions } = this.props;

    const parentItem = dimensions.find(
      (item) => item.id === selectedItem.rootId
    );

    return parentItem?.name;
  };

  getSelectedDimensionItem = () => {
    const { selectedItem, isAppTab } = this.state;

    if (!selectedItem) {
      return null;
    }
    const { dimensions, applications } = this.props;
    const items = isAppTab ? applications : dimensions;

    if (selectedItem.rootId) {
      const parent = items.find((item) => item.id === selectedItem.rootId);

      return parent?.children?.find((item) => item.id === selectedItem.id);
    }

    return items.find((item) => item.id === selectedItem.id);
  };

  getSelectedAllocation = () => {
    const { selectedItem } = this.state;
    const { allocations } = this.props;

    if (!selectedItem || !allocations || allocations.length === 0) {
      return null;
    }

    const selectedAllocation = allocations.find(
      (item) => item.dimensionId === selectedItem.id
    );

    return selectedAllocation;
  };

  getAllocatedDimensionList = () => {
    const { dimensions, applications, allocations } = this.props;
    const { isAppTab } = this.state;

    const categories = isAppTab ? applications : dimensions;

    const categoriesWithAllocations: IAllocatedDimensionGroup[] = JSON.parse(
      JSON.stringify(categories)
    );

    allocations.forEach((allocation) => {
      if (allocation.rootId) {
        const parent = categoriesWithAllocations.find(
          (item) => item.id === allocation.rootId
        );

        if (parent && parent.children) {
          const child = parent.children.find(
            (item) => item.id === allocation.dimensionId
          );

          if (child) {
            child.isAllocated = true;
            child.isInvalid = !isAllocationItemValid(allocation.items);
          }
        }
      } else {
        const parent = categoriesWithAllocations.find(
          (item) => item.id === allocation.dimensionId
        );

        if (parent) {
          parent.isAllocated = true;
          parent.isInvalid = !isAllocationItemValid(allocation.items);
        }
      }
    });

    return categoriesWithAllocations;
  };

  handleProductsChange = (items: IAllocation[]) => {
    const { selectedItem, isAppTab } = this.state;

    if (!selectedItem) {
      return null;
    }

    const { onAllocationChange, spendType, selectedYear, dimensions } =
      this.props;
    const activeAllocation = this.getSelectedAllocation();
    let updatedAllocation;

    if (activeAllocation) {
      updatedAllocation = {
        ...activeAllocation,
        items,
      };
    } else {
      updatedAllocation = {
        contribution: contributionField[spendType],
        type: (!isAppTab && dimensions[0] && dimensions[0].type) || null,
        dimensionId: selectedItem.id,
        dimensionName: this.getSelectedDimensionItem()?.name || '',
        rootId: selectedItem.rootId || null,
        level: isAppTab ? 0 : selectedItem.rootId ? 2 : 1,
        year: selectedYear,
        items,
      };
    }

    onAllocationChange(updatedAllocation);
  };

  handleProductRemove = (appId: number) => {
    const { onAllocationChange } = this.props;
    const activeAllocation = this.getSelectedAllocation();

    if (activeAllocation) {
      onAllocationChange({
        ...activeAllocation,
        items: activeAllocation.items.filter((item) => item.id !== appId),
      });
    }
  };

  isGroupSelectionDisabled = () => {
    const { dimensions } = this.props;

    return (
      dimensions[0] && dimensions[0].type === DimensionTypeEnum.ServiceTowers
    );
  };

  render() {
    const { selectedItem, isAppTab } = this.state;
    const {
      spendType,
      format,
      products,
      onReuseAllocation,
      isDimensionsLoading,
      dimensions,
    } = this.props;
    const selectedItemSpend = this.getSelectedDimensionItem()?.spend || 0;
    const selectedItemName = this.getSelectedDimensionItem()?.name;
    const nonAppDimension =
      (dimensions[0] && (dimensions[0].type as DimensionTypeEnum)) ||
      DimensionTypeEnum.ServiceTowers;

    return (
      <React.Fragment>
        <MainWrapper>
          <LeftSection>
            <TabsWrapper>
              <ApButtonTab
                isSelected={isAppTab}
                onClick={() => this.setState({ isAppTab: true })}
              >
                Application spend
              </ApButtonTab>
              <ApButtonTab
                isSelected={!isAppTab}
                onClick={() => this.setState({ isAppTab: false })}
              >
                Non-application spend
              </ApButtonTab>
            </TabsWrapper>
            <DimensionControls
              onReuseAllocation={onReuseAllocation}
              dimension={
                isAppTab ? 'Applications' : dimensionLabels[nonAppDimension]
              }
              message={
                !isAppTab
                  ? 'Non-application spend includes all spend that was not allocated to applications.'
                  : undefined
              }
            />
            <OverlayContainer>
              {isDimensionsLoading && <Overlay color={colors.grey1} />}
              <DimensionList
                selection={selectedItem}
                onSelect={this.handleItemSelect}
                data={this.getAllocatedDimensionList()}
                format={format}
                disableGroupSelection={this.isGroupSelectionDisabled()}
              />
            </OverlayContainer>
          </LeftSection>
          <MiddleContainer>
            <ScrollWrapper>
              <MiddleContent
                spend={selectedItemSpend}
                format={format}
                spendType={spendType}
                parentName={this.getSelectedParentName()}
                name={selectedItemName}
                allocations={this.getSelectedAllocation()?.items}
                onAppRemove={this.handleProductRemove}
                dimensionId={this.getSelectedAllocation()?.dimensionId}
                title="product"
                body="Map your application and non-application spend to products using the left-side panel"
                othersLabel="Non-product cost"
              />
            </ScrollWrapper>
          </MiddleContainer>
          <SideContainer>
            {!selectedItem && <Overlay />}
            <AllocationList
              items={products}
              allocations={this.getSelectedAllocation()?.items}
              onChange={this.handleProductsChange}
              title="Product name"
            />
          </SideContainer>
        </MainWrapper>
      </React.Fragment>
    );
  }
}
